import { Skeleton } from '@/components/ui/skeleton'
import { Card, CardContent, CardHeader } from '@/components/ui/card' // Import Card components
import { cn } from '@/lib/utils'

// Enhanced Skeleton with better contrast
const EnhancedSkeleton = ({
	className,
	...props
}: React.HTMLAttributes<HTMLDivElement>) => (
	<Skeleton
		className={cn('bg-slate-200 dark:bg-slate-700', className)} // Use specific colors
		{...props}
	/>
)

const ChartSkeleton = () => (
	<Card className="bg-card">
		{' '}
		{/* Use Card for structure */}
		<CardHeader>
			<EnhancedSkeleton className="h-6 w-48 mb-4" />{' '}
			{/* Slightly larger title skeleton */}
		</CardHeader>
		<CardContent>
			<EnhancedSkeleton className="w-full h-[260px]" /> {/* Adjusted height */}
		</CardContent>
	</Card>
)

const TableSkeleton = () => (
	<Card className="bg-card lg:col-span-2">
		{' '}
		{/* Span 2 columns on large screens */}
		<CardHeader>
			<EnhancedSkeleton className="h-6 w-48 mb-4" />{' '}
			{/* Slightly larger title skeleton */}
		</CardHeader>
		<CardContent className="space-y-4 px-0">
			{' '}
			{/* Remove default CardContent padding */}
			{[...Array(4)].map((_, i) => (
				<div key={i} className="flex items-center gap-4 px-6 py-3">
					<EnhancedSkeleton className="h-8 w-16 rounded-md" />{' '}
					{/* Type Badge */}
					<div className="space-y-2 flex-1">
						<EnhancedSkeleton className="h-4 w-3/4" /> {/* Summary */}
					</div>
					<EnhancedSkeleton className="h-4 w-24 hidden sm:block" />{' '}
					{/* Submitted By */}
					<EnhancedSkeleton className="h-4 w-20 text-right" /> {/* Date */}
				</div>
			))}
		</CardContent>
	</Card>
)

const NeedsAttentionSkeleton = () => (
	<Card className="bg-card">
		<CardHeader>
			<EnhancedSkeleton className="h-6 w-40 mb-4" />
		</CardHeader>
		<CardContent className="space-y-4">
			<div className="flex justify-between items-center">
				<EnhancedSkeleton className="h-4 w-24" />
				<EnhancedSkeleton className="h-6 w-8" />
			</div>
			<div className="flex justify-between items-center">
				<EnhancedSkeleton className="h-4 w-32" />
				<EnhancedSkeleton className="h-6 w-8" />
			</div>
			<div className="pt-4 border-t">
				<EnhancedSkeleton className="h-5 w-36 mb-3" />
				<div className="space-y-3">
					<EnhancedSkeleton className="h-10 w-full" />
					<EnhancedSkeleton className="h-10 w-full" />
				</div>
			</div>
		</CardContent>
	</Card>
)

export const DashboardSkeleton = () => {
	return (
		<div className="space-y-6 sm:space-y-8 p-4 sm:p-6 md:p-8">
			{/* Header Skeleton */}
			<div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
				<div className="space-y-2">
					<EnhancedSkeleton className="h-8 w-48" /> {/* Dashboard Title */}
					<EnhancedSkeleton className="h-4 w-64" /> {/* Description */}
				</div>
				<EnhancedSkeleton className="h-9 w-36 rounded-lg" />{' '}
				{/* TimePeriodSelector */}
			</div>

			{/* Charts Skeleton */}
			<div className="grid gap-6 sm:gap-8 grid-cols-1 md:grid-cols-2">
				<ChartSkeleton />
				<ChartSkeleton />
			</div>

			{/* Tables Skeleton */}
			<div className="grid gap-6 sm:gap-8 grid-cols-1 lg:grid-cols-3">
				<TableSkeleton />
				<NeedsAttentionSkeleton />
			</div>
		</div>
	)
}
